/**
 * Analytics Service
 *
 * Centralized analytics wrapper that sends events to multiple providers:
 * - Google Analytics 4 (gtag)
 * - Facebook Pixel (fbq)
 * - Microsoft Bing UET (uetq)
 * - Google Ads Conversions (gtag)
 *
 * IMPORTANT: Respects Google Consent Mode v2 and GDPR requirements.
 * - window.tracVis is set by the ConsentManager based on user consent
 * - For GDPR countries: requires user consent before tracking
 * - For non-GDPR countries: tracking enabled by default
 *
 * The ConsentManager handles:
 * - Setting gtag('consent', 'default', {...}) before any tracking loads
 * - Updating consent state when user accepts/rejects
 * - Setting window.tracVis appropriately
 *
 * Uses Segment-compatible event naming for easy future migration.
 * https://segment.com/docs/connections/spec/ecommerce/v2/
 *
 * Usage:
 *   import Analytics from '../services/analytics'
 *
 *   Analytics.track('Product Added', {
 *     sku: 'SKU-123',
 *     name: 'Product Name',
 *     category: 'Floor Heating',
 *     price: 299.00,
 *     quantity: 1,
 *     currency: 'USD'
 *   })
 */

import { debug } from '../utils/debug'
import { getLocale, getCurrencyForLocale } from '../utils/locale'

class Analytics {
  // Google Ads Conversion IDs
  // Account: AW-1061033593
  static GOOGLE_ADS_PURCHASE_CONVERSION = 'AW-1061033593/JY65COGFxpYBEPms-PkD'
  static GOOGLE_ADS_LEAD_CONVERSION = 'AW-1061033593/gim2CJe7j4IBEPms-PkD'
  static GOOGLE_ADS_CALL_ME_NOW_CONVERSION = 'AW-1061033593/zh9aCIufvZYBEPms-PkD'

  // Debug mode - uses global debug utility
  static get _debug() {
    return debug.isEnabled
  }

  static set _debug(value) {
    if (value) debug.enable()
    else debug.disable()
  }

  /**
   * Check if Global Privacy Control (GPC) is enabled
   * Required for CCPA compliance - blocks "sale" of data (marketing pixels)
   * @returns {boolean}
   */
  static get hasGlobalPrivacyControl() {
    return navigator.globalPrivacyControl === true
  }

  /**
   * Check if analytics tracking is enabled
   * window.tracVis is set immediately by _tracking_init.html.erb (reads cookie)
   * @returns {boolean}
   */
  static get isEnabled() {
    return window.tracVis === true
  }

  /**
   * Check if marketing/advertising pixels can be fired
   * Respects GPC signal which blocks ad tracking under CCPA
   * @returns {boolean}
   */
  static get isMarketingEnabled() {
    // GPC blocks marketing/ad pixels (CCPA "Do Not Sell" equivalent)
    if (this.hasGlobalPrivacyControl) return false
    return this.isEnabled
  }

  /**
   * Get default currency based on locale
   * Returns null if locale unavailable - caller must handle this
   */
  static get defaultCurrency() {
    const locale = getLocale()
    if (!locale) {
      debug.warn('analytics', 'Cannot determine currency - locale unavailable')
      return null
    }
    return getCurrencyForLocale(locale)
  }

  /**
   * Main tracking method - routes to appropriate handler
   * No queueing needed - window.tracVis is set immediately from cookie
   * @param {string} eventName - Segment-compatible event name
   * @param {object} properties - Event properties
   */
  static track(eventName, properties = {}) {
    // Set currency from locale if not provided - skip currency if locale unavailable
    if (!properties.currency) {
      const currency = this.defaultCurrency
      if (currency) {
        properties.currency = currency
      }
      // If currency is null (locale unavailable), we still track but without currency
    }

    debug('Analytics', 'track:', eventName, properties)

    // Check if tracking is enabled (set immediately by _tracking_init.html.erb)
    if (!this.isEnabled) {
      debug('Analytics', 'Tracking disabled, skipping:', eventName)
      return
    }

    // Fire the event
    this._fireEvent(eventName, properties)
  }

  /**
   * Fire an event to all analytics platforms
   * @private
   */
  static _fireEvent(eventName, properties) {
    debug('Analytics', '_fireEvent:', eventName)
    const handlers = {
      // Ecommerce Events
      'Product Added': () => this.trackProductAdded(properties),
      'Product Removed': () => this.trackProductRemoved(properties),
      'Cart Viewed': () => this.trackCartViewed(properties),
      'Checkout Started': () => this.trackCheckoutStarted(properties),
      'Checkout Step Completed': () => this.trackCheckoutStepCompleted(properties),
      'Payment Info Entered': () => this.trackPaymentInfoEntered(properties),
      'Order Completed': () => this.trackOrderCompleted(properties),

      // Lead Events
      'Lead Submitted': () => this.trackLeadSubmitted(properties),

      // Quote Builder Events
      'Quote Requested': () => this.trackQuoteRequested(properties),
      'Quote Shared': () => this.trackQuoteShared(properties),
      'Quote Viewed': () => this.trackQuoteViewed(properties),
      'Quote Added to Cart': () => this.trackQuoteAddedToCart(properties),
      'Selections Added to Quote': () => this.trackSelectionsAddedToQuote(properties),
      'Call Me Now Requested': () => this.trackCallMeNowRequested(properties),

      // Custom UX Events
      'Shipping Info Added': () => this.trackShippingInfoAdded(properties),
      'Contact Engagement': () => this.trackContactEngagement(properties),
    }

    const handler = handlers[eventName]
    if (handler) {
      handler()
    } else {
      if (this._debug) {
        console.warn(`[Analytics] Unknown event: ${eventName}`)
      }
      this.trackCustomEvent(eventName, properties)
    }
  }

  /**
   * Identify a user (for future CDP integration)
   * @param {string} userId - User identifier
   * @param {object} traits - User traits
   */
  static identify(userId, traits = {}) {
    // GA4 user properties
    if (typeof window.gtag === 'function') {
      window.gtag('set', 'user_properties', traits)
      if (userId) {
        window.gtag('set', { user_id: userId })
      }
    }

    // Facebook Pixel
    if (typeof window.fbq === 'function' && userId) {
      window.fbq('init', window.FB_PIXEL_ID, { external_id: userId })
    }

    // Bing UET doesn't support user identification in the same way
  }

  /**
   * Page view tracking
   * @param {string} name - Page name
   * @param {object} properties - Page properties
   */
  static page(name, properties = {}) {
    // GA4 handles page views automatically with enhanced measurement
    // Facebook Pixel tracks PageView on load

    // For SPAs or manual tracking:
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: name,
        page_location: properties.url || window.location.href,
        page_path: properties.path || window.location.pathname,
      })
    }
  }

  // ============================================
  // Ecommerce Event Handlers
  // ============================================

  /**
   * Product Added to Cart
   * https://segment.com/docs/connections/spec/ecommerce/v2/#product-added
   */
  static trackProductAdded({ sku, name, category, price, quantity, currency, imageUrl }) {
    const value = (price || 0) * (quantity || 1)
    debug('Analytics', 'trackProductAdded:', { sku, name, value, currency })

    // GA4: add_to_cart
    // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_to_cart
    this.gtag('event', 'add_to_cart', {
      currency,
      value,
      items: [{
        item_id: sku,
        item_name: name,
        item_category: category,
        price: price || 0,
        quantity: quantity || 1,
      }]
    })

    // Facebook Pixel: AddToCart
    // https://developers.facebook.com/docs/meta-pixel/reference#standard-events
    this.fbq('track', 'AddToCart', {
      content_name: name,
      content_category: category,
      content_ids: [sku],
      content_type: 'product',
      value,
      currency,
    })

    // Microsoft Bing UET
    this.uetq('event', 'add_to_cart', {
      ecomm_prodid: sku,
      ecomm_pagetype: 'product',
      revenue_value: value,
      currency,
    })
  }

  /**
   * Product Removed from Cart
   * https://segment.com/docs/connections/spec/ecommerce/v2/#product-removed
   */
  static trackProductRemoved({ sku, name, category, price, quantity, currency }) {
    const value = (price || 0) * (quantity || 1)

    // GA4: remove_from_cart
    this.gtag('event', 'remove_from_cart', {
      currency,
      value,
      items: [{
        item_id: sku,
        item_name: name,
        item_category: category,
        price: price || 0,
        quantity: quantity || 1,
      }]
    })

    // Facebook Pixel: custom event (no standard equivalent)
    this.fbq('trackCustom', 'RemoveFromCart', {
      content_name: name,
      content_ids: [sku],
      content_type: 'product',
      value,
      currency,
    })

    // Microsoft Bing UET: custom event
    this.uetq('event', 'remove_from_cart', {
      ecomm_prodid: sku,
      revenue_value: value,
      currency,
    })
  }

  /**
   * Cart Viewed
   * https://segment.com/docs/connections/spec/ecommerce/v2/#cart-viewed
   */
  static trackCartViewed({ cartId, products, currency, value }) {
    const numItems = products?.reduce((sum, p) => sum + (p.quantity || 1), 0) || 0

    // GA4: view_cart
    this.gtag('event', 'view_cart', {
      currency,
      value: value || 0,
      items: this.formatItems(products),
    })

    // Facebook Pixel: ViewContent (for cart)
    this.fbq('track', 'ViewContent', {
      content_ids: products?.map(p => p.sku) || [],
      content_type: 'product',
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET
    this.uetq('event', 'view_cart', {
      ecomm_pagetype: 'cart',
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Checkout Started
   * https://segment.com/docs/connections/spec/ecommerce/v2/#checkout-started
   */
  static trackCheckoutStarted({ orderId, value, currency, products }) {
    // GA4: begin_checkout
    this.gtag('event', 'begin_checkout', {
      currency,
      value: value || 0,
      items: this.formatItems(products),
    })

    // Facebook Pixel: InitiateCheckout
    this.fbq('track', 'InitiateCheckout', {
      content_ids: products?.map(p => p.sku) || [],
      content_type: 'product',
      num_items: products?.reduce((sum, p) => sum + (p.quantity || 1), 0) || 0,
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET - 'cart' is the closest valid ecomm_pagetype for checkout
    // Valid values: home, category, product, searchresults, cart, purchase, other
    this.uetq('event', 'begin_checkout', {
      ecomm_pagetype: 'cart',
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Checkout Step Completed
   * https://segment.com/docs/connections/spec/ecommerce/v2/#checkout-step-completed
   */
  static trackCheckoutStepCompleted({ step, shippingMethod, paymentMethod, currency, value }) {
    // GA4 uses separate events for shipping and payment
    if (step === 'shipping' || shippingMethod) {
      // GA4
      this.gtag('event', 'add_shipping_info', {
        currency,
        value: value || 0,
        shipping_tier: shippingMethod,
      })

      // Facebook Pixel: custom event
      this.fbq('trackCustom', 'AddShippingInfo', {
        value: value || 0,
        currency,
        shipping_tier: shippingMethod,
      })

      // Microsoft Bing UET
      this.uetq('event', 'add_shipping_info', {
        revenue_value: value || 0,
        currency,
      })
    }

    if (step === 'payment' || paymentMethod) {
      // GA4
      this.gtag('event', 'add_payment_info', {
        currency,
        value: value || 0,
        payment_type: paymentMethod,
      })

      // Facebook Pixel: AddPaymentInfo
      this.fbq('track', 'AddPaymentInfo', {
        content_category: paymentMethod,
        value: value || 0,
        currency,
      })

      // Microsoft Bing UET
      this.uetq('event', 'add_payment_info', {
        revenue_value: value || 0,
        currency,
      })
    }
  }

  /**
   * Shipping Info Added (custom event for checkout flow)
   */
  static trackShippingInfoAdded({ currency, value, shippingMethod, source }) {
    // GA4: add_shipping_info
    this.gtag('event', 'add_shipping_info', {
      currency,
      value: value || 0,
      shipping_tier: shippingMethod,
    })

    // Facebook Pixel: AddShippingInfo (custom event, no standard equivalent)
    this.fbq('trackCustom', 'AddShippingInfo', {
      value: value || 0,
      currency,
      shipping_tier: shippingMethod,
    })

    // Microsoft Bing UET
    this.uetq('event', 'add_shipping_info', {
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Contact Engagement (user interacts with contact methods)
   * Low-value engagement tracking - GA4 only
   */
  static trackContactEngagement({ action }) {
    // GA4: custom event for contact engagement
    this.gtag('event', 'contact_engagement', {
      event_category: 'Contact',
      event_label: action,
    })

    // Note: Not sending to Facebook/Bing as these are low-value engagement events
    // If you want to track these on other platforms, uncomment below:
    // this.fbq('trackCustom', 'ContactEngagement', { action })
    // this.uetq('event', 'contact_engagement', { event_label: action })
  }

  /**
   * Payment Info Entered
   * https://segment.com/docs/connections/spec/ecommerce/v2/#payment-info-entered
   */
  static trackPaymentInfoEntered({ currency, value, paymentMethod }) {
    // GA4: add_payment_info
    this.gtag('event', 'add_payment_info', {
      currency,
      value: value || 0,
      payment_type: paymentMethod,
    })

    // Facebook Pixel: AddPaymentInfo
    this.fbq('track', 'AddPaymentInfo', {
      content_category: paymentMethod,
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET
    this.uetq('event', 'add_payment_info', {
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Order Completed (Purchase)
   * https://segment.com/docs/connections/spec/ecommerce/v2/#order-completed
   */
  static trackOrderCompleted({ orderId, revenue, tax, shipping, currency, products, coupon }) {
    // GA4: purchase
    // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#purchase
    this.gtag('event', 'purchase', {
      transaction_id: orderId,
      value: revenue || 0,
      tax: tax || 0,
      shipping: shipping || 0,
      currency,
      coupon,
      items: this.formatItems(products),
    })

    // Google Ads Purchase Conversion
    this.gtag('event', 'conversion', {
      send_to: this.GOOGLE_ADS_PURCHASE_CONVERSION,
      value: revenue || 0,
      currency,
      transaction_id: orderId,
    })

    // Facebook Pixel: Purchase
    // https://developers.facebook.com/docs/meta-pixel/reference#standard-events
    this.fbq('track', 'Purchase', {
      content_ids: products?.map(p => p.sku) || [],
      content_type: 'product',
      num_items: products?.reduce((sum, p) => sum + (p.quantity || 1), 0) || 0,
      value: revenue || 0,
      currency,
    })

    // Microsoft Bing UET: purchase
    this.uetq('event', 'purchase', {
      revenue_value: revenue || 0,
      currency,
      transaction_id: orderId,
    })
  }

  // ============================================
  // Lead Event Handlers
  // ============================================

  /**
   * Lead Submitted
   * https://segment.com/docs/connections/spec/b2b-saas/#lead-captured
   */
  static trackLeadSubmitted({ leadType, source, value, currency }) {
    // GA4: generate_lead
    this.gtag('event', 'generate_lead', {
      currency,
      value: value || 0,
      lead_source: source,
    })

    // Google Ads Lead Conversion
    this.gtag('event', 'conversion', {
      send_to: this.GOOGLE_ADS_LEAD_CONVERSION,
    })

    // Facebook Pixel: Lead
    this.fbq('track', 'Lead', {
      content_name: leadType || source,
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET
    this.uetq('event', 'submit_lead_form', {
      event_category: 'Lead',
      event_label: leadType || source,
    })
  }

  // ============================================
  // Quote Builder Event Handlers
  // ============================================

  /**
   * Quote Requested (Get Quotes button clicked)
   */
  static trackQuoteRequested({ quoteId, roomType, floorType, area, currency, value }) {
    // GA4: custom event
    this.gtag('event', 'quote_requested', {
      quote_id: quoteId,
      room_type: roomType,
      floor_type: floorType,
      area,
      currency,
      value: value || 0,
    })

    // Facebook Pixel: custom event
    this.fbq('trackCustom', 'QuoteRequested', {
      quote_id: quoteId,
      room_type: roomType,
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET
    this.uetq('event', 'quote_requested', {
      event_category: 'QuoteBuilder',
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Quote Shared (user clicks share button)
   */
  static trackQuoteShared({ quoteId, currency, value }) {
    // GA4: custom event
    this.gtag('event', 'quote_shared', {
      quote_id: quoteId,
      currency,
      value: value || 0,
    })

    // Facebook Pixel: custom event
    this.fbq('trackCustom', 'QuoteShared', {
      quote_id: quoteId,
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET
    this.uetq('event', 'quote_shared', {
      event_category: 'QuoteBuilder',
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Quote Viewed (user views a saved quote)
   */
  static trackQuoteViewed({ quoteId, value, currency, itemCount }) {
    // GA4: custom event
    this.gtag('event', 'quote_viewed', {
      quote_id: quoteId,
      value: value || 0,
      currency,
      item_count: itemCount,
    })

    // Facebook Pixel: ViewContent
    this.fbq('track', 'ViewContent', {
      content_type: 'quote',
      content_ids: [quoteId],
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET - using 'other' as ecomm_pagetype since 'quote' isn't valid
    // Valid values: home, category, product, searchresults, cart, purchase, other
    this.uetq('event', 'quote_viewed', {
      ecomm_pagetype: 'other',
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Quote Added to Cart (Buy Now from quote)
   */
  static trackQuoteAddedToCart({ quoteId, value, currency, products }) {
    // GA4: add_to_cart (treating quote as adding items)
    this.gtag('event', 'add_to_cart', {
      currency,
      value: value || 0,
      items: this.formatItems(products) || [{ item_id: quoteId, item_name: `Quote ${quoteId}`, price: value }],
    })

    // Facebook Pixel: AddToCart
    this.fbq('track', 'AddToCart', {
      content_type: 'product',
      content_ids: products?.map(p => p.sku) || [quoteId],
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET - using 'other' as ecomm_pagetype since 'quote' isn't valid
    // Valid values: home, category, product, searchresults, cart, purchase, other
    this.uetq('event', 'add_to_cart', {
      ecomm_prodid: quoteId,
      ecomm_pagetype: 'other',
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Selections Added to Quote (user adds items to existing quote)
   */
  static trackSelectionsAddedToQuote({ quoteId, value, currency }) {
    // GA4: custom event
    this.gtag('event', 'selections_added_to_quote', {
      quote_id: quoteId,
      currency,
      value: value || 0,
    })

    // Facebook Pixel: custom event
    this.fbq('trackCustom', 'SelectionsAddedToQuote', {
      quote_id: quoteId,
      value: value || 0,
      currency,
    })

    // Microsoft Bing UET
    this.uetq('event', 'selections_added_to_quote', {
      event_category: 'QuoteBuilder',
      revenue_value: value || 0,
      currency,
    })
  }

  /**
   * Call Me Now Requested (user submits phone number for callback)
   * High-value conversion - fires Google Ads conversion
   */
  static trackCallMeNowRequested({ source, currency }) {
    // GA4: custom event
    this.gtag('event', 'call_me_now_requested', {
      event_category: 'Contact',
      event_label: source || 'unknown',
    })

    // Google Ads Conversion - "Call Me Now" is a high-value lead
    this.gtag('event', 'conversion', {
      send_to: this.GOOGLE_ADS_CALL_ME_NOW_CONVERSION,
    })

    // Facebook Pixel: Lead (callback request is a lead)
    this.fbq('track', 'Lead', {
      content_name: 'Call Me Now',
      content_category: source || 'Contact',
    })

    // Microsoft Bing UET
    this.uetq('event', 'call_me_now', {
      event_category: 'Contact',
      event_label: source || 'unknown',
    })
  }

  // ============================================
  // Custom Event Handler
  // ============================================

  /**
   * Track a custom event (fallback for unmapped events)
   */
  static trackCustomEvent(eventName, properties = {}) {
    // Convert to snake_case for GA4
    const ga4EventName = eventName.toLowerCase().replace(/\s+/g, '_')

    this.gtag('event', ga4EventName, properties)

    // Facebook custom event
    this.fbq('trackCustom', eventName, properties)
  }

  // ============================================
  // Provider Wrappers (with safety checks)
  // ============================================

  /**
   * Safe wrapper for Google Analytics 4 (gtag)
   * Respects window.tracVis tracking consent
   */
  static gtag(...args) {
    if (!this.isEnabled) {
      debug('Analytics', 'gtag skipped - not enabled')
      return
    }
    if (typeof window.gtag === 'function') {
      debug('Analytics', 'gtag ->', ...args)
      window.gtag(...args)
    } else {
      debug('Analytics', 'gtag - window.gtag not available')
    }
  }

  /**
   * Safe wrapper for Facebook Pixel (fbq)
   * Respects consent and GPC signal (marketing pixel)
   */
  static fbq(...args) {
    if (!this.isMarketingEnabled) {
      debug('Analytics', 'fbq - marketing disabled, skipping')
      return
    }
    if (typeof window.fbq === 'function') {
      debug('Analytics', 'fbq ->', ...args)
      window.fbq(...args)
    } else {
      debug('Analytics', 'fbq - window.fbq not available')
    }
  }

  /**
   * Safe wrapper for Microsoft Bing UET (uetq)
   * Respects consent and GPC signal (marketing pixel)
   */
  static uetq(action, eventName, params = {}) {
    if (!this.isMarketingEnabled) {
      debug('Analytics', 'uetq - marketing disabled, skipping')
      return
    }
    if (typeof window.uetq !== 'undefined' && Array.isArray(window.uetq)) {
      debug('Analytics', 'uetq ->', action, eventName, params)
      window.uetq.push(action, eventName, params)
    } else {
      debug('Analytics', 'uetq - window.uetq not available')
    }
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Format products array for GA4 items format
   */
  static formatItems(products) {
    if (!products || !Array.isArray(products)) return []

    return products.map((product, index) => ({
      item_id: product.sku || product.id,
      item_name: product.name,
      item_category: product.category,
      price: product.price || 0,
      quantity: product.quantity || 1,
      index,
    }))
  }

  /**
   * Debug mode - logs all events to console
   */
  static enableDebug() {
    this._debug = true
    console.log('[Analytics] Debug mode enabled')
  }

  static disableDebug() {
    this._debug = false
  }
}

// Export for ES modules
export default Analytics

// Also attach to window for non-module usage (ERB templates)
if (typeof window !== 'undefined') {
  window.Analytics = Analytics
}

